import { sortByName, sortAsc } from '../utils/sortOptions';
import PasswordInput from '../components/PasswordInput';
import { formatPhone, formatCnic } from '../utils/employeeFormatters';
import { createPortal } from 'react-dom';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConfirmModal from '../components/ConfirmModal';
import { PencilIcon, PlusIcon, UserGroupIcon, XMarkIcon, ArrowRightOnRectangleIcon, MagnifyingGlassIcon, ClockIcon, LockClosedIcon, LockOpenIcon, ClipboardDocumentIcon, CheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const parseDate = (d) => {
  if (!d) return '';
  if (typeof d === 'string') return d.slice(0, 10);
  const dt = new Date(d);
  const y = dt.getFullYear();
  const mo = String(dt.getMonth()+1).padStart(2,'0');
  const day = String(dt.getDate()).padStart(2,'0');
  return y + '-' + mo + '-' + day;
};


const ini = (n='') => n.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2)||'S';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .sr*{box-sizing:border-box;} .sr{font-family:'Sora',sans-serif;color:#1a1d2e;}
  .sr-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.75rem;flex-wrap:wrap;gap:12px;}
  .sr-title{font-size:1.6rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;}
  .sr-sub{font-size:.78rem;color:#9094a8;margin-top:2px;}
  .btn{padding:8px 18px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;}
  .btn-blue{background:#3b6ef8;color:white;} .btn-blue:hover{box-shadow:0 4px 14px rgba(59,110,248,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-orange{background:#fff7ed;color:#f97316;border:1px solid #fed7aa;} .btn-orange:hover{background:#f97316;color:white;}
  .panel{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;margin-bottom:1rem;}
  .panel-hdr{display:flex;align-items:center;gap:10px;padding:16px 20px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;}
  .panel-icon{width:34px;height:34px;border-radius:10px;background:#eef2ff;color:#3b6ef8;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .panel-icon svg{width:17px;height:17px;stroke-width:1.8;}

  /* Search/filter bar */
  .filter-bar{display:flex;gap:10px;padding:12px 16px;border-bottom:1px solid #f0f2f8;flex-wrap:wrap;align-items:center;}
  .search-wrap{display:flex;align-items:center;gap:8px;background:#f8f9fc;border:1.5px solid #e0e4f0;border-radius:10px;padding:7px 12px;flex:1;min-width:180px;}
  .search-wrap:focus-within{border-color:#3b6ef8;background:white;}
  .search-wrap svg{width:15px;height:15px;stroke:#9094a8;stroke-width:2;flex-shrink:0;}
  .search-inp{border:none;background:transparent;font-family:'Sora',sans-serif;font-size:.82rem;color:#1a1d2e;outline:none;width:100%;}
  .filter-sel{font-family:'Sora',sans-serif;font-size:.8rem;padding:8px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;min-width:130px;}
  .filter-sel:focus{border-color:#3b6ef8;background:white;}

  .tbl{width:100%;border-collapse:collapse;font-size:.8rem;}
  .tbl thead tr{background:#f8f9fc;border-bottom:1px solid #eaecf4;}
  .tbl th{padding:11px 16px;text-align:center !important;font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;white-space:nowrap;}
  .tbl th:last-child{text-align:right;}
  .tbl tbody tr{border-bottom:1px solid #f3f5fb;transition:background .12s;}
  .tbl tbody tr:last-child{border-bottom:none;} .tbl tbody tr:hover{background:#fafbff;}
  .tbl td{padding:12px 16px;color:#3a3f58;vertical-align:middle;} .tbl td:last-child{text-align:right;}
  .cards{display:none;padding:10px;}
  .card{background:#fafbff;border-radius:12px;border:1px solid #e8ebf4;padding:13px;margin-bottom:8px;}
  .card-top{display:flex;align-items:flex-start;justify-content:space-between;margin-bottom:8px;gap:8px;}
  .card-row{display:flex;justify-content:space-between;align-items:center;font-size:.76rem;padding:4px 0;border-bottom:1px solid #f0f2f8;gap:8px;}
  .card-row:last-of-type{border-bottom:none;}
  .card-lbl{color:#9094a8;font-weight:600;white-space:nowrap;}
  .card-acts{display:flex;gap:6px;margin-top:8px;}
  .nc{display:flex;align-items:center;gap:10px;}
  .av{width:32px;height:32px;border-radius:9px;background:linear-gradient(135deg,#3b6ef8,#8b5cf6);display:flex;align-items:center;justify-content:center;font-size:.65rem;font-weight:700;color:white;flex-shrink:0;}
  .nm{font-weight:600;color:#1a1d2e;font-size:.82rem;} .ne{font-size:.68rem;color:#9094a8;margin-top:1px;}
  .tag{display:inline-block;padding:2px 8px;border-radius:99px;font-size:.68rem;font-weight:600;}
  .tag-p{background:#f3f0ff;color:#8b5cf6;} .tag-b{background:#eef2ff;color:#3b6ef8;}
  .mono{font-family:'JetBrains Mono',monospace;font-size:.75rem;color:#6b7194;}
  .act-btn{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;margin-left:4px;transition:.13s;}
  .act-btn svg{width:14px;height:14px;stroke-width:1.8;}
  .act-btn.edit{color:#3b6ef8;} .act-btn.edit:hover{background:#eef2ff;border-color:#3b6ef8;}
  .act-btn.del{color:#ef4444;}   .act-btn.del:hover{background:#fff1f1;border-color:#fecaca;}
  .act-btn.left{color:#f97316;}  .act-btn.left:hover{background:#fff7ed;border-color:#fed7aa;}
  .act-btn.deactivate{color:#ef4444;} .act-btn.deactivate:hover{background:#fef2f2;border-color:#fecaca;}
  .act-btn.activate{color:#10c98e;} .act-btn.activate:hover{background:#ecfdf5;border-color:#a7f3d0;}
  .empty{text-align:center;padding:52px 20px;color:#b0b5cc;font-size:.82rem;}
  .spin{width:16px;height:16px;border:2px solid #e4e7f0;border-top-color:#3b6ef8;border-radius:50%;animation:sp .7s linear infinite;display:inline-block;}
  @keyframes sp{to{transform:rotate(360deg);}}
  .pg{display:flex;align-items:center;justify-content:center;gap:6px;padding:16px 0;flex-wrap:wrap;}
  .pg-btn{padding:6px 14px;border-radius:9px;border:1px solid #e0e4f0;background:#f8f9fc;font-size:.78rem;font-weight:600;font-family:'Sora',sans-serif;color:#6b7194;cursor:pointer;}
  .pg-btn:hover:not(:disabled){background:#eef2ff;border-color:#3b6ef8;color:#3b6ef8;} .pg-btn:disabled{opacity:.4;cursor:not-allowed;}
  .pg-info{font-family:'JetBrains Mono',monospace;font-size:.75rem;color:#9094a8;padding:0 8px;}
  .overlay{position:fixed;inset:0;background:rgba(10,12,28,.55);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:1000;padding:20px;}
  .modal{background:white;border-radius:20px;width:100%;max-width:560px;max-height:90vh;border:1px solid #e8ebf4;box-shadow:0 24px 64px rgba(0,0,0,.18);overflow:hidden;display:flex;flex-direction:column;animation:mIn .25s ease;}
  @keyframes mIn{from{opacity:0;transform:translateY(16px) scale(.97)}to{opacity:1;transform:none}}
  .m-hdr{display:flex;align-items:center;justify-content:space-between;padding:18px 22px;border-bottom:1px solid #f0f2f8;flex-shrink:0;gap:8px;}
  .m-hdr-l{display:flex;align-items:center;gap:10px;}
  .m-icon{width:34px;height:34px;border-radius:10px;background:#eef2ff;color:#3b6ef8;display:flex;align-items:center;justify-content:center;}
  .m-icon svg{width:17px;height:17px;stroke-width:1.8;}
  .m-icon.orange{background:#fff7ed;color:#f97316;}
  .m-title{font-size:.95rem;font-weight:700;color:#0f1123;} .m-sub{font-size:.72rem;color:#9094a8;margin-top:1px;}
  .m-close{width:30px;height:30px;border-radius:8px;border:1px solid #e8ebf4;background:#f8f9fc;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#9094a8;}
  .m-close svg{width:14px;height:14px;stroke-width:2;}
  .m-body{padding:22px;overflow-y:auto;flex:1;} .m-foot{display:flex;justify-content:flex-end;gap:8px;padding:16px 22px;border-top:1px solid #f0f2f8;flex-shrink:0;flex-wrap:wrap;}
  .fg{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .ff{display:flex;flex-direction:column;gap:5px;} .ff.full{grid-column:1/-1;}
  .fl{font-size:.7rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .fi,.fs{font-family:'Sora',sans-serif;font-size:.82rem;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:#f8f9fc;color:#1a1d2e;outline:none;width:100%;transition:.15s;}
  .fi:focus,.fs:focus{border-color:#3b6ef8;background:white;box-shadow:0 0 0 3px rgba(59,110,248,.08);}
  .fi.mono{font-family:'JetBrains Mono',monospace;}
  .fi[readonly]{background:#f0f2f7;color:#9094a8;cursor:not-allowed;border-color:#e8ebf4;}
  .fdiv{grid-column:1/-1;display:flex;align-items:center;gap:10px;margin:4px 0;}
  .fdiv-lbl{font-size:.65rem;font-weight:700;color:#c0c5dc;text-transform:uppercase;letter-spacing:.09em;white-space:nowrap;}
  .fdiv-line{flex:1;height:1px;background:#f0f2f8;}
  .auto-badge{font-size:.64rem;font-weight:600;padding:1px 7px;border-radius:99px;background:#ecfdf5;color:#10c98e;margin-left:6px;vertical-align:middle;}
  .readonly-badge{font-size:.64rem;font-weight:600;padding:1px 7px;border-radius:99px;background:#f0f2f7;color:#9094a8;margin-left:6px;vertical-align:middle;}
  .left-warn{background:#fff7ed;border:1px solid #fed7aa;border-radius:10px;padding:10px 14px;font-size:.78rem;color:#92400e;margin-bottom:14px;}
  .sr-fade{animation:srFade .4s ease both;}
  @keyframes srFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .pem-opt{display:flex;align-items:flex-start;gap:10px;padding:11px 14px;border-radius:10px;border:1.5px solid;cursor:pointer;margin-bottom:8px;transition:.15s;}
  .pem-opt-title{font-weight:600;font-size:.82rem;margin-bottom:2px;}
  .pem-opt-sub{font-size:.73rem;color:#6b7194;}
  @media(max-width:768px){
    .sr-title{font-size:1.2rem;} .tbl-wrap{display:none!important;} .cards{display:block;}
    .m-body{padding:16px;} .m-foot{justify-content:stretch;} .m-foot .btn{flex:1;justify-content:center;}
    .fg{grid-template-columns:1fr;} .ff.full{grid-column:1;} .fdiv{grid-column:1;}
    .filter-bar{flex-direction:column;} .filter-sel{width:100%;}
  }
`;

const FD = ({label}) => <div className="fdiv"><span className="fdiv-lbl">{label}</span><div className="fdiv-line"/></div>;

const Students = () => {
  const navigate = useNavigate();
  const [students,        setStudents]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [page,            setPage]            = useState(1);
  const [total,           setTotal]           = useState(1);
  const [modal,           setModal]           = useState(false);
  const [leftModal,       setLeftModal]       = useState(null);
  const [parentExitModal, setParentExitModal] = useState(null);
  const [editing,         setEditing]         = useState(null);
  const [classes,         setClasses]         = useState([]);
  const [leftForm,        setLeftForm]        = useState({ leftReason:'TC', leftNote:'', leftAt:'' });
  const [pemSaving,       setPemSaving]       = useState(false);
  const [statusModal,     setStatusModal]     = useState(null); // { student, active }
  const [statusSaving,    setStatusSaving]    = useState(false);
  const [parentInfoModal, setParentInfoModal] = useState(null); // student whose Parent & Guardian info is shown
  const [copiedField,     setCopiedField]     = useState('');
  // The admin students list endpoint doesn't return login-active state, so we
  // track it locally per student id after a successful toggle (defaults active).
  const [statusOverrides, setStatusOverrides] = useState({});

  // Search/filter state
  const [search,        setSearch]        = useState('');
  const [filterClass,   setFilterClass]   = useState('');
  const [filterSection, setFilterSection] = useState('');
  const hasLoaded    = useRef(false);
  const searchTimer = useRef(null);

  const [form, setForm] = useState({
    name:'', email:'', password:'', admissionNo:'', class:'', section:'',
    rollNo:'', parentName:'', parentContact:'', parentEmail:'', parentCnic:'',
    dateOfBirth:'', address:'', joiningDate:'', leftDate:'',
  });

  useEffect(() => { load(); loadClasses(); }, [page]);

  // Debounced search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load(); }, 350);
  }, [search, filterClass, filterSection]);

  const load = async () => {
    try {
      if (!hasLoaded.current) setLoading(true);
      const params = new URLSearchParams({ page });
      if (search)        params.append('search',  search);
      if (filterClass)   params.append('classId', filterClass);
      if (filterSection) params.append('section', filterSection);
      const { data } = await api.get('/admin/students?' + params.toString());
      setStudents(data.students || []);
      hasLoaded.current = true;
      setTotal(data.totalPages || 1);
    } catch { toast.error('Failed to load'); }
    finally   { setLoading(false); }
  };

  const loadClasses = async () => {
    try { const { data } = await api.get('/admin/classes'); setClasses(Array.isArray(data) ? data : []); } catch {}
  };

  const classIdRef = useRef('');
  const selectedClass   = classes.find(c => String(c._id) === String(form.class));
  const sections        = selectedClass?.sections || [];
  const filterSections  = classes.find(c => String(c._id) === String(filterClass))?.sections || [];

  const openAdd = async () => {
    setEditing(null);
    setForm({ name:'', email:'', password:'', admissionNo:'', class:'', section:'', rollNo:'', parentName:'', parentContact:'', parentEmail:'', parentCnic:'', dateOfBirth:'', address:'', joiningDate: new Date().toISOString().split('T')[0], leftDate:'' });
    setModal(true);
    try {
      const { data } = await api.get('/admin/students/next-admission');
      setForm(prev => ({ ...prev, admissionNo: data.admissionNo }));
    } catch {}
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.user?.name||'', email: s.user?.email||'', password:'',
      admissionNo: s.admissionNo||'',
      class: String(s.class?._id||s.class||''),
      section: s.section||'',
      rollNo: s.rollNo ? String(s.rollNo).padStart(2,'0') : '',
      parentName: s.parentName||'', parentContact: s.parentContact||'',
      parentEmail: s.parentEmail||'', parentCnic: s.parentCnic||'',
      dateOfBirth: s.dateOfBirth ? s.dateOfBirth.split('T')[0] : '',
      joiningDate: s.joiningDate ? s.joiningDate.split('T')[0] : '',
      address: s.address||'', leftDate:'',
    });
    setModal(true);
  };

  const openLeft = (s) => {
    setLeftModal(s);
    setLeftForm({ leftReason:'TC', leftNote:'', leftAt: new Date().toISOString().split('T')[0] });
  };

  const handleClassChange = (e) => {
    const classId = e.target.value;
    classIdRef.current = classId;
    setForm(prev => ({ ...prev, class: classId, section:'', rollNo:'' }));
  };

  const handleSectionChange = async (e) => {
    const section = e.target.value;
    const classId = classIdRef.current || form.class;
    setForm(prev => ({ ...prev, section, rollNo:'' }));
    if (classId && section && !editing) {
      try {
        const { data } = await api.get('/admin/students/next-roll?classId=' + classId + '&section=' + section);
        setForm(prev => ({ ...prev, rollNo: String(data.rollNo).padStart(2,'0') }));
      } catch {}
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim())        { toast.error('Name is required'); return; }
    if (!form.email.trim())       { toast.error('Email is required'); return; }
    if (!editing && !form.password.trim()) { toast.error('Password is required'); return; }
    if (!form.admissionNo.trim()) { toast.error('Admission No is required'); return; }
    if (!form.class)              { toast.error('Class is required'); return; }
    if (!form.section)            { toast.error('Section is required'); return; }
    if (!form.rollNo)             { toast.error('Roll No is required'); return; }
    try {
      if (editing) { await api.put('/admin/students/' + editing._id, form); toast.success('Student updated'); }
      else         { const { data } = await api.post('/admin/students', form); toast.success(data?.message || 'Student created', data?.studentPortalEnabled===false ? { duration: 7000 } : undefined); }
      setModal(false); classIdRef.current = ''; load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const submitLeft = async () => {
    if (!leftForm.leftReason) return toast.error('Select a reason');
    try {
      const { data: parentStatus } = await api.get('/admin/students/' + leftModal._id + '/parent-status');
      if (parentStatus.hasParent && parentStatus.suggestDeactivate) {
        setParentExitModal({ student: leftModal, parentInfo: parentStatus, leftForm: { ...leftForm }, deactivate: true });
        setLeftModal(null);
      } else {
        await api.put('/admin/students/' + leftModal._id + '/left', leftForm);
        toast.success((leftModal.user?.name || 'Student') + ' marked as left');
        setLeftModal(null); load();
      }
    } catch (err) {
      if (err.response?.status === 404) {
        try { await api.put('/admin/students/' + leftModal._id + '/left', leftForm); toast.success('Marked as left'); setLeftModal(null); load(); }
        catch (e2) { toast.error(e2.response?.data?.message || 'Failed'); }
      } else { toast.error(err.response?.data?.message || 'Failed'); }
    }
  };

  const submitParentExit = async () => {
    setPemSaving(true);
    try {
      await api.put('/admin/students/' + parentExitModal.student._id + '/exit-with-parent', {
        deactivateParentAccount: parentExitModal.deactivate,
        exitDate: parentExitModal.leftForm.leftAt, exitReason: parentExitModal.leftForm.leftReason,
      });
      toast.success(parentExitModal.deactivate ? 'Student left & parent account deactivated' : 'Student marked as left');
      setParentExitModal(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setPemSaving(false); }
  };

  const isStudentActive = (s) => statusOverrides[s._id] !== undefined ? statusOverrides[s._id] : true;

  const openStatusModal = (s, active) => setStatusModal({ student: s, active });

  const copyField = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      setTimeout(() => setCopiedField(f => f === label ? '' : f), 1500);
    } catch { toast.error('Could not copy — select and copy manually'); }
  };

  const confirmStatusAction = async () => {
    if (!statusModal) return;
    const { student, active } = statusModal;
    const userId = student.user?._id;
    setStatusSaving(true);
    try {
      await api.put(`/admin/user-status/${userId}`, { active });
      toast.success(active ? 'Student activated' : 'Student deactivated');
      setStatusOverrides(prev => ({ ...prev, [student._id]: active }));
      setStatusModal(null); load();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setStatusSaving(false); }
  };

  return (
    <>
      <style>{style}</style>
      <div className="sr sr-fade">
        <div className="sr-hdr">
          <div><h1 className="sr-title">Students</h1><p className="sr-sub">Manage student enrolments, records and parent details.</p></div>
          <button className="btn btn-blue" onClick={openAdd}><PlusIcon />Add Student</button>
        </div>

        <div className="panel">
          <div className="panel-hdr">
            <div className="panel-icon"><UserGroupIcon /></div>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:'.88rem'}}>All Students</div>
              <div style={{fontSize:'.72rem',color:'#9094a8'}}>{loading ? 'Loading…' : students.length + ' students on this page'}</div>
            </div>
          </div>

          {/* Search + Filter Bar */}
          <div className="filter-bar">
            <div className="search-wrap">
              <MagnifyingGlassIcon />
              <input className="search-inp" placeholder="Search by name or admission no…"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <select className="filter-sel" value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSection(''); }}>
              <option value="">All Classes</option>
              {sortByName(classes).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <select className="filter-sel" value={filterSection} onChange={e => setFilterSection(e.target.value)} disabled={!filterClass}>
              <option value="">{filterClass ? 'All Sections' : '— Section —'}</option>
              {sortAsc(filterSections).map(s => <option key={s} value={s}>Section {s}</option>)}
            </select>
            {(search || filterClass || filterSection) && (
              <button className="btn btn-ghost" style={{padding:'7px 12px',fontSize:'.76rem'}}
                onClick={() => { setSearch(''); setFilterClass(''); setFilterSection(''); }}>
                Clear
              </button>
            )}
          </div>

          {loading
            ? <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px 0',color:'#9094a8',fontSize:'.82rem'}}><span className="spin"/>Loading…</div>
            : students.length === 0
            ? <div className="empty">{search||filterClass ? 'No students match your search.' : 'No students found.'}</div>
            : <>
                <div className="tbl-wrap" style={{overflowX:'auto'}}>
                  <table className="tbl">
                    <thead><tr>
                      <th style={{minWidth:160}}>Student</th>
                      <th style={{minWidth:130,textAlign:'center'}}>Admission No</th>
                      <th style={{minWidth:100,textAlign:'center'}}>Class</th>
                      <th style={{minWidth:80,textAlign:'center'}}>Section</th>
                      <th style={{minWidth:80,textAlign:'center'}}>Roll No</th>
                      <th style={{minWidth:100,textAlign:'center'}}>Joining</th>
                      <th style={{minWidth:130}}>Parent</th>
                      <th style={{textAlign:'right',minWidth:100}}>Actions</th>
                    </tr></thead>
                    <tbody>
                      {students.map(s => (
                        <tr key={s._id}>
                          <td><div className="nc"><div className="av">{ini(s.user?.name)}</div><div><div className="nm">{s.user?.name}</div><div className="ne">{s.user?.email}</div></div></div></td>
                          <td style={{textAlign:'center'}}><span className="mono">{s.admissionNo}</span></td>
                          <td style={{textAlign:'center'}}><span className="tag tag-p">{s.class?.name}</span></td>
                          <td style={{textAlign:'center'}}><span className="tag tag-b">{s.section}</span></td>
                          <td style={{textAlign:'center'}}><span className="mono">{String(s.rollNo||'').padStart(2,'0')}</span></td>
                          <td style={{textAlign:'center'}}><span style={{fontFamily:"'JetBrains Mono',monospace",fontSize:'.74rem',color:'#6b7194'}}>{s.joiningDate ? new Date(s.joiningDate).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}).replace(/ /g,'-') : '—'}</span></td>
                          <td>{s.parentName
                            ? <div style={{cursor:'pointer'}} onClick={() => setParentInfoModal(s)} title="View full parent/guardian info">
                                <div style={{fontSize:'.78rem',color:'#3b6ef8',fontWeight:600}}>{s.parentName}</div>
                                {s.parentContact && <div className="mono" style={{fontSize:'.68rem'}}>{s.parentContact}</div>}
                              </div>
                            : <span style={{color:'#c0c5dc',fontSize:'.76rem'}}>—</span>}</td>
                          <td>
                            <button className="act-btn edit" title="Edit"         onClick={() => openEdit(s)}><PencilIcon /></button>
                            <button className="act-btn left" title="Mark as Left" onClick={() => openLeft(s)}><ArrowRightOnRectangleIcon /></button>
                            <button className="act-btn edit" title="Full History" onClick={() => navigate(`/students/${s._id}/history`)}><ClockIcon /></button>
                            {isStudentActive(s)
                              ? <button className="act-btn deactivate" title="Deactivate" onClick={() => openStatusModal(s, false)}><LockClosedIcon /></button>
                              : <button className="act-btn activate" title="Activate" onClick={() => openStatusModal(s, true)}><LockOpenIcon /></button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="cards">
                  {students.map(s => (
                    <div key={s._id} className="card">
                      <div className="card-top"><div className="nc"><div className="av">{ini(s.user?.name)}</div><div><div className="nm">{s.user?.name}</div><div className="ne">{s.user?.email}</div></div></div></div>
                      <div className="card-row"><span className="card-lbl">Admission No</span><span className="mono">{s.admissionNo}</span></div>
                      <div className="card-row"><span className="card-lbl">Class</span><span className="tag tag-p">{s.class?.name}</span></div>
                      <div className="card-row"><span className="card-lbl">Section</span><span className="tag tag-b">{s.section}</span></div>
                      <div className="card-row"><span className="card-lbl">Roll No</span><span className="mono">{String(s.rollNo||'').padStart(2,'0')}</span></div>
                      {s.parentName && <div className="card-row" style={{cursor:'pointer'}} onClick={() => setParentInfoModal(s)}><span className="card-lbl">Parent</span><span style={{fontSize:'.76rem',color:'#3b6ef8',fontWeight:600}}>{s.parentName}</span></div>}
                      <div className="card-acts">
                        <button className="act-btn edit" onClick={() => openEdit(s)}><PencilIcon /></button>
                        <button className="act-btn left" onClick={() => openLeft(s)}><ArrowRightOnRectangleIcon /></button>
                        <button className="act-btn edit" onClick={() => navigate(`/students/${s._id}/history`)}><ClockIcon /></button>
                        {isStudentActive(s)
                          ? <button className="act-btn deactivate" title="Deactivate" onClick={() => openStatusModal(s, false)}><LockClosedIcon /></button>
                          : <button className="act-btn activate" title="Activate" onClick={() => openStatusModal(s, true)}><LockOpenIcon /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              </>
          }
          {total > 1 && (
            <div className="pg">
              <button className="pg-btn" onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}>← Prev</button>
              <span className="pg-info">Page {page} of {total}</span>
              <button className="pg-btn" onClick={() => setPage(p => Math.min(total,p+1))} disabled={page===total}>Next →</button>
            </div>
          )}
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {modal && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="m-hdr">
              <div className="m-hdr-l"><div className="m-icon"><UserGroupIcon /></div><div><div className="m-title">{editing ? 'Edit Student' : 'Add Student'}</div><div className="m-sub">{editing ? 'Update info.' : 'Admission no and roll auto-filled.'}</div></div></div>
              <button className="m-close" onClick={() => setModal(false)}><XMarkIcon /></button>
            </div>
            <form onSubmit={submit} style={{display:'contents'}} autoComplete="off">
              <div className="m-body">
                <div className="fg">
                  <FD label="Account" />
                  <div className="ff"><label className="fl">Full Name *</label><input className="fi" value={form.name} onChange={e => setForm(p => ({...p, name:e.target.value}))} required /></div>
                  <div className="ff"><label className="fl">Email *</label><input type="email" className="fi" value={form.email} onChange={e => setForm(p => ({...p, email:e.target.value}))} required /></div>
                  {!editing && <div className="ff full"><label className="fl">Password *</label><PasswordInput className="fi" autoComplete="new-password" value={form.password} onChange={e => setForm(p => ({...p, password:e.target.value}))} required /></div>}

                  <FD label="Academic Details" />
                  {/* #9 — Admission No read-only (auto-generated ADM-YEAR-NNNN) */}
                  <div className="ff">
                    <label className="fl">Admission No <span className="auto-badge">auto</span><span className="readonly-badge">read-only</span></label>
                    <input className="fi mono" value={form.admissionNo} readOnly />
                  </div>
                  <div className="ff"><label className="fl">Date of Birth</label><input type="date" className="fi" value={form.dateOfBirth} onChange={e => setForm(p => ({...p, dateOfBirth:e.target.value}))} /></div>
                  <div className="ff">
                    <label className="fl">Class *</label>
                    <select className="fs" value={form.class} onChange={handleClassChange} required>
                      <option value="">— Select Class —</option>
                      {classes.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="ff">
                    <label className="fl">Section * {!editing && form.class && form.section && <span className="auto-badge">roll auto</span>}</label>
                    {sections.length > 0
                      ? <select className="fs" value={form.section} onChange={handleSectionChange} required>
                          <option value="">— Select Section —</option>
                          {sections.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      : <input className="fi" placeholder={form.class ? 'No sections' : 'Select class first'} value={form.section} onChange={e => handleSectionChange({ target:{ value:e.target.value } })} required />
                    }
                  </div>
                  {/* #10 — Roll No 2 digits, read-only auto */}
                  <div className="ff">
                    <label className="fl">Roll No <span className="auto-badge">auto</span><span className="readonly-badge">read-only</span></label>
                    <input className="fi mono" value={form.rollNo} readOnly />
                  </div>
                  <div className="ff"><label className="fl">Address</label><input className="fi" value={form.address} onChange={e => setForm(p => ({...p, address:e.target.value}))} /></div>
                  <div className="ff"><label className="fl">Joining Date</label><input type="date" className="fi" value={form.joiningDate} onChange={e => setForm(p => ({...p, joiningDate:e.target.value}))} /></div>
                  {editing && <div className="ff"><label className="fl">Left Date</label><input type="date" className="fi" value={form.leftDate} onChange={e => setForm(p => ({...p, leftDate:e.target.value}))} /></div>}

                  <FD label="Parent / Guardian" />
                  <div className="ff"><label className="fl">Parent Name</label><input className="fi" value={form.parentName} onChange={e => setForm(p => ({...p, parentName:e.target.value}))} /></div>
                  <div className="ff"><label className="fl">Parent Contact</label><input className="fi mono" value={form.parentContact} onChange={e => setForm(p => ({...p, parentContact:formatPhone(e.target.value)}))} /></div>
                  <div className="ff full"><label className="fl">Parent Email</label><input type="email" className="fi" value={form.parentEmail} onChange={e => setForm(p => ({...p, parentEmail:e.target.value}))} /></div>
                  <div className="ff full"><label className="fl">Parent/Guardian CNIC</label><input className="fi mono" placeholder="12345-1234567-1" maxLength={15} value={form.parentCnic||''} onChange={e => setForm(p => ({...p, parentCnic:formatCnic(e.target.value)}))} /></div>
                </div>
              </div>
              <div className="m-foot">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-blue">{editing ? 'Update' : 'Add Student'}</button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}

      {/* ── Mark as Left Modal ── */}
      {leftModal && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setLeftModal(null)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon orange"><ArrowRightOnRectangleIcon /></div>
                <div><div className="m-title">Mark Student as Left</div><div className="m-sub">{leftModal.user?.name}</div></div>
              </div>
              <button className="m-close" onClick={() => setLeftModal(null)}><XMarkIcon /></button>
            </div>
            <div className="m-body">
              <div className="left-warn">⚠️ Once marked as left, this student will be removed from all active lists. Historical records are preserved.</div>
              <div className="fg">
                <div className="ff full">
                  <label className="fl">Reason for Leaving</label>
                  <select className="fs" value={leftForm.leftReason} onChange={e => setLeftForm(p => ({...p, leftReason:e.target.value}))}>
                    <option value="TC">Transfer Certificate (TC)</option>
                    <option value="dropout">Dropout</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="ff full"><label className="fl">Left Date</label><input type="date" className="fi" value={leftForm.leftAt} onChange={e => setLeftForm(p => ({...p, leftAt:e.target.value}))} /></div>
                <div className="ff full"><label className="fl">Note (optional)</label><textarea className="fi" rows={2} style={{resize:'vertical'}} value={leftForm.leftNote} onChange={e => setLeftForm(p => ({...p, leftNote:e.target.value}))} /></div>
              </div>
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={() => setLeftModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={submitLeft}>Mark as Left</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* ── Parent Deactivate Confirm Modal ── */}
      {parentExitModal && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setParentExitModal(null)}>
          <div className="modal" style={{maxWidth:440}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon orange">
                  <svg width="17" height="17" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </div>
                <div><div className="m-title">Parent Account</div><div className="m-sub">{parentExitModal.student?.user?.name} is leaving</div></div>
              </div>
              <button className="m-close" onClick={() => setParentExitModal(null)}><XMarkIcon /></button>
            </div>
            <div className="m-body">
              <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:10,padding:'11px 14px',fontSize:'.78rem',color:'#92400e',marginBottom:16,lineHeight:1.6}}>
                <strong>{parentExitModal.parentInfo.parent.name}</strong> has no other active children. Do you want to deactivate their parent portal access?
              </div>
              <div className="pem-opt" style={{borderColor:parentExitModal.deactivate?'#ef4444':'#e0e4f0',background:parentExitModal.deactivate?'#fef2f2':'#f8f9fc'}} onClick={() => setParentExitModal(p => ({...p,deactivate:true}))}>
                <input type="radio" readOnly checked={parentExitModal.deactivate} style={{marginTop:3,accentColor:'#ef4444',flexShrink:0}}/>
                <div><div className="pem-opt-title" style={{color:'#ef4444'}}>Yes — deactivate parent account</div><div className="pem-opt-sub">Parent can't log in. Can be reactivated later.</div></div>
              </div>
              <div className="pem-opt" style={{borderColor:!parentExitModal.deactivate?'#10c98e':'#e0e4f0',background:!parentExitModal.deactivate?'#ecfdf5':'#f8f9fc'}} onClick={() => setParentExitModal(p => ({...p,deactivate:false}))}>
                <input type="radio" readOnly checked={!parentExitModal.deactivate} style={{marginTop:3,accentColor:'#10c98e',flexShrink:0}}/>
                <div><div className="pem-opt-title" style={{color:'#10c98e'}}>No — keep parent account active</div><div className="pem-opt-sub">Parent can still log in.</div></div>
              </div>
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={() => setParentExitModal(null)}>Cancel</button>
              <button className="btn btn-orange" onClick={submitParentExit} disabled={pemSaving}>{pemSaving?'Saving…':'Mark as Left'}</button>
            </div>
          </div>
        </div>

      , document.body)}

      <ConfirmModal
        open={!!statusModal}
        title={statusModal?.active ? 'Activate Student' : 'Deactivate Student'}
        subtitle={statusModal?.student?.user?.name}
        message={statusModal?.active
          ? "This student's portal login will be re-enabled immediately."
          : "This student will immediately lose portal login access. You can activate them again at any time."}
        confirmLabel={statusModal?.active ? 'Activate' : 'Deactivate'}
        danger={!statusModal?.active}
        onConfirm={confirmStatusAction}
        onCancel={() => setStatusModal(null)}
        loading={statusSaving}
      />

      {/* ── Parent & Guardian Info Modal — every field with its own copy button ── */}
      {parentInfoModal && createPortal(
        <div className="overlay" onClick={e => e.target === e.currentTarget && setParentInfoModal(null)}>
          <div className="modal" style={{maxWidth:420}}>
            <div className="m-hdr">
              <div className="m-hdr-l">
                <div className="m-icon"><UserGroupIcon /></div>
                <div><div className="m-title">Parent / Guardian Info</div><div className="m-sub">{parentInfoModal.user?.name}</div></div>
              </div>
              <button className="m-close" onClick={() => setParentInfoModal(null)}><XMarkIcon /></button>
            </div>
            <div className="m-body">
              {[
                ['Name',    parentInfoModal.parentName],
                ['Contact', parentInfoModal.parentContact],
                ['Email',   parentInfoModal.parentEmail],
                ['CNIC',    parentInfoModal.parentCnic],
              ].map(([label, value]) => (
                <div key={label} style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:10,padding:'10px 0',borderBottom:'1px solid #f0f2f8'}}>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:'.65rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase',letterSpacing:'.06em'}}>{label}</div>
                    <div className={label==='Contact'||label==='CNIC' ? 'mono' : ''} style={{fontSize:'.84rem',color:'#1a1d2e',marginTop:2,wordBreak:'break-word'}}>{value || '—'}</div>
                  </div>
                  {!!value && (
                    <button type="button" className="act-btn edit" title="Copy" style={{flexShrink:0}} onClick={() => copyField(label, value)}>
                      {copiedField===label ? <CheckIcon style={{color:'#10c98e'}} /> : <ClipboardDocumentIcon />}
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="m-foot">
              <button className="btn btn-ghost" onClick={() => setParentInfoModal(null)}>Close</button>
            </div>
          </div>
        </div>
      , document.body)}
    </>
  );
};

export default Students;